import atmosphere from 'atmosphere.js'
import { useCallback, useEffect, useRef } from 'react'

import { useAtmosphereTransportCache } from './useAtmosphereTransportCache'

export interface AtmosphereVariables<T> {
  topic: string
  onMessage?(body: T, response, push: (message: T) => void): any
  onConnect?(): void
}

const url = `${window.location.protocol}//${window.location.host}/atmosphere/`

export default <T>({ topic, onMessage, onConnect }: AtmosphereVariables<T>) => {
  const connected = useRef(false)
  const subSocketRef = useRef<any>(null)
  const configRef = useRef<any>(null)

  const { currentTransport, setCurrentTransport } = useAtmosphereTransportCache()

  const disconnect = useCallback(() => {
    connected.current = false
    atmosphere.unsubscribeUrl(url + topic)
    subSocketRef.current = null
  }, [topic])

  const push = useCallback((message: T) => {
    if (connected.current) {
      subSocketRef.current?.push(JSON.stringify(message))
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Atmosphere - Attempted to push message to topic ${configRef.current?.url} while not connected.`)
    }
  }, [])

  const connect = useCallback(() => {
    disconnect()
    subSocketRef.current = atmosphere.subscribe(configRef.current)
  }, [disconnect])

  // Funções recebidas por props, devem usar o useCallback + useEffect para atualizar os handlers do atmosphere conforme essas funcões mudam

  const handleOnMessage = useCallback(
    (request) => {
      connected.current &&
        onMessage &&
        request.responseBody &&
        onMessage(JSON.parse(request.responseBody), request, push)
    },
    [onMessage, push]
  )

  useEffect(() => {
    if (subSocketRef.current) {
      subSocketRef.current.request.onMessage = handleOnMessage
    }
  }, [handleOnMessage])

  const handleOnOpen = useCallback(() => {
    connected.current = true
    onConnect?.()
  }, [onConnect])

  useEffect(() => {
    if (subSocketRef.current) {
      subSocketRef.current.request.onOpen = handleOnOpen
    }
  }, [handleOnOpen])

  configRef.current = {
    url: url + topic,
    transport: currentTransport,
    fallbackTransport: undefined,
    logLevel: process.env.NODE_ENV === 'development' ? 'info' : 'error',
    contentType: 'application/json; charset=UTF-8',
    trackMessageLength: true,
    reconnectInterval: 200,
    pollingInterval: 100,
    enableProtocol: currentTransport === 'websocket',

    onOpen: handleOnOpen,
    onClose: () => {
      connected.current = false
    },
    onReconnect: () => {
      connected.current = true
    },
    onError: () => {
      connected.current = false
    },
    onReopen: () => {
      connected.current = true
    },
    onFailureToReconnect: () => {
      connected.current = false
    },
    onClientTimeout: () => {
      connected.current = false
    },
    onTransportFailure: () => {
      connected.current = false
      if (configRef.current.transport === 'websocket') {
        configRef.current.transport = 'long-polling'
        setCurrentTransport('long-polling')
        connect()
      } else {
        // Tentou fallback para long-polling e falhou

        // eslint-disable-next-line no-console
        console.error(`Atmosphere - Failed websocket and long-polling on topic ${topic}`)
        disconnect()
      }
    },
    onMessage: handleOnMessage,
  }

  useEffect(() => {
    connect()
  }, [connect])

  // called on destroy component
  useEffect(() => () => disconnect(), [disconnect])

  return {
    push,
  }
}
